import { useTranslation } from 'react-i18next';

import { CHANNEL_SHORT_NAME_MAP, TChannelProtocol } from '@/entities/channel';
import { getBillingDisplayPrices } from '@/features/billing/model/partnerDiscount';
import { TariffSummary } from '@/features/billing/ui/tariffSummary/TariffSummary';
import { ITariffGridDto, ITariffPlanDto } from '@/shared/api/subscriptions';
import { Stack } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { CustomTypography } from '@/shared/ui/Typography';

import { useTariffGrid } from '../model/useTariffGrid';

import { PeriodCard } from './periodCard/PeriodCard';
import { TariffCard } from './tariffCard/TariffCard';
import { CardItem, SelectableCardGroup } from './selectableCardGroup';

type TSelectTariffProps = {
  channelProtocol?: TChannelProtocol;
  selectedTariff?: ITariffGridDto;
  selectedPlan?: ITariffPlanDto;
  onTariffChange: (tariff: ITariffGridDto) => void;
  onPeriodChange: (period: ITariffPlanDto) => void;
  isPartnerPayment?: boolean;
  partnerDiscountRate?: number;
};

export const SelectTariff = ({
  channelProtocol,
  selectedTariff,
  selectedPlan,
  onTariffChange,
  onPeriodChange,
  isPartnerPayment,
  partnerDiscountRate,
}: TSelectTariffProps) => {
  const { t } = useTranslation();
  const { data: rawData, isPending } = useTariffGrid(channelProtocol);

  const data = rawData
    ? rawData.filter(
        (tariff, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.tariff_info.tariff_id === tariff.tariff_info.tariff_id ||
              t.tariff_info.name === tariff.tariff_info.name
          )
      )
    : rawData;

  if (data && !selectedTariff) {
    const firstTariff = data[0];
    if (firstTariff) {
      onTariffChange(firstTariff);
      if (!selectedPlan) {
        onPeriodChange(firstTariff.tariff_grid_plan[0]);
      }
    }
  }
  const handleTariffchange = (tariff: ITariffGridDto) => {
    onTariffChange(tariff);

    const currentTariffPlanBySelectedPeriod = tariff.tariff_grid_plan.find(
      (plan) => plan.interval === selectedPlan?.interval
    );

    onPeriodChange(
      currentTariffPlanBySelectedPeriod || tariff.tariff_grid_plan[0]
    );
  };

  if (isPending) {
    return <Loader containerSize="small" />;
  }

  if (!data || data.length === 0) {
    return (
      <CustomTypography variant="body2" color="text.secondary">
        {t('billing:errors.selectTariffAndPlan')}
      </CustomTypography>
    );
  }

  if (!selectedTariff || !selectedPlan) {
    return null;
  }

  const { totalPrice, originalPrice } = getBillingDisplayPrices(
    selectedPlan.final_price,
    Boolean(isPartnerPayment),
    partnerDiscountRate
  );

  return (
    <Stack spacing={3}>
      <SelectableCardGroup title={t('billing:selectTariff')}>
        {data.map((tariff) => {
          const {
            tariff_id: id,
            price,
            name,
            can_write_first: canWriteFirst,
            dialog_count: dialogCount,
            group_chats_enabled: groupChatsEnabled,
            priority_technical_support: priorityTechnicalSupport,
          } = tariff.tariff_info;

          const currentTariffPlanBySelectedPeriod =
            tariff.tariff_grid_plan.find(
              (plan) => plan.interval === selectedPlan.interval
            );

          const discountPrice =
            currentTariffPlanBySelectedPeriod!.discount_price;

          return (
            <CardItem
              key={id}
              selected={selectedTariff.tariff_info.tariff_id === id}
              onClick={() => {
                handleTariffchange(tariff);
              }}
            >
              <TariffCard
                name={name}
                price={discountPrice}
                oldPrice={discountPrice !== price ? price : undefined}
                selected={selectedTariff.tariff_info.tariff_id === id}
                canWriteFirst={canWriteFirst}
                dialogCount={dialogCount}
                groupChatsEnabled={groupChatsEnabled}
                priorityTechnicalSupport={priorityTechnicalSupport}
                channelName={
                  channelProtocol ? CHANNEL_SHORT_NAME_MAP[channelProtocol] : ''
                }
              />
            </CardItem>
          );
        })}
      </SelectableCardGroup>

      <SelectableCardGroup title={t('billing:selectPeriod')}>
        {selectedTariff.tariff_grid_plan.map((plan) => {
          const { interval, discount } = plan;
          return (
            <CardItem
              key={`${selectedTariff.tariff_info.tariff_id}-${interval}`}
              selected={selectedPlan.interval === plan.interval}
              onClick={() => onPeriodChange(plan)}
            >
              <PeriodCard
                label={interval}
                selected={selectedPlan.interval === plan.interval}
                discountText={discount}
              />
            </CardItem>
          );
        })}
      </SelectableCardGroup>

      <TariffSummary
        totalPrice={totalPrice}
        fullPrice={originalPrice ?? selectedPlan.full_price}
      />
    </Stack>
  );
};
